import { useQuery } from '@apollo/client';
import { useCallback, useMemo, useState } from 'react';

import { GET_SERVICE_RUNS_MY_DAY_PREVIEW } from '../../../common/graphql/serviceRun';
import { useUserContext } from '../../../common/utils/context';
import {
	MY_DAY_STEP_STATUS,
	buildEndOfDaySteps,
	buildPerStopSteps,
	buildStartOfDaySteps,
	getNextAction,
	isDocketDoneForMyDay,
	isDocketHandledForMyDay,
	isServiceRunStarted,
} from '../../../common/utils/myDay/driverDaySteps';
import { getFullMachineName } from '../../../common/utils/machine';
import { getLocalTime } from '../../../common/utils/time/time';
import { getUserName } from '../../../common/utils/user/getUserName';
import { formatDate } from '../../../common/utils/utils';
import { useRefreshPage } from '../../hooks';
import { useOfflineQueue } from '../../offline';

const COUNTABLE_STEP_STATUSES = [
	MY_DAY_STEP_STATUS.DONE,
	MY_DAY_STEP_STATUS.TODO,
	MY_DAY_STEP_STATUS.BLOCKED,
];

const getEmployeeName = (employee) => {
	const name = getUserName(employee);
	return ['No user', 'Un-named user'].includes(name)
		? employee?.username || 'Unknown employee'
		: name;
};

const getNextActionCopy = ({ nextAction, activeDocket }) => {
	if (!nextAction) {
		return {
			title: 'No next action',
			description: 'The selected run has no remaining required My Day action.',
		};
	}

	if (nextAction.type === 'stop') {
		const stopName = getFullMachineName(
			activeDocket?.machine,
			activeDocket?.machine?.place,
			activeDocket?.machine?.location,
		) || 'Current stop';
		return {
			title: nextAction.step?.label || stopName,
			description: nextAction.step?.description || `Continue the guided work at ${stopName}.`,
		};
	}

	return {
		title: nextAction.step?.label || 'Continue My Day',
		description: nextAction.step?.description || 'Continue to the next guided My Day phase.',
	};
};

/**
 * Read-only data of an employee's recorded day for the admin preview (classic
 * EmployeeMyDayPreviewView and the redesigned EmployeeDayPreviewView): the
 * employee's runs today, the selected run and stop, and the start/stop/end
 * steps from driverDaySteps. Params: employee from EmployeeMyDaySelectorDialog.
 */
const useEmployeeDayPreview = (employee) => {
	const { tenant, tenantSettings } = useUserContext();
	const { isOnline } = useOfflineQueue();
	const timezone = tenant?.timezone;
	const dateKey = employee?.dateKey || getLocalTime(timezone).format('YYYY-MM-DD');
	const allowedRunIds = useMemo(() => (
		(employee?.runIds || []).map(String).filter(Boolean)
	), [employee?.runIds]);
	const allowedRunIdSet = useMemo(() => new Set(allowedRunIds), [allowedRunIds]);
	const [selectedRunId, setSelectedRunId] = useState(null);
	const [selectedDocketId, setSelectedDocketId] = useState(null);
	const [previewedAt] = useState(() => Date.now());
	const { data, loading, error, refetch } = useQuery(GET_SERVICE_RUNS_MY_DAY_PREVIEW, {
		variables: {
			filter: {
				serviceRunIds: allowedRunIds,
			},
		},
		skip: !allowedRunIds.length,
		// Keep another employee's entities separate from the admin's offline My Day cache.
		fetchPolicy: 'no-cache',
		errorPolicy: 'all',
	});
	const refetchPreview = useCallback(() => refetch(), [refetch]);
	const [refreshPreview, isRefreshing] = useRefreshPage(refetchPreview);
	const detailedRuns = useMemo(() => (
		(data?.getServiceRuns?.items || [])
			.filter(run => (
				allowedRunIdSet.has(String(run?.id)) &&
				String(run?.driver?.id) === String(employee?.id) &&
				run?.planned &&
				getLocalTime(timezone, run.planned).format('YYYY-MM-DD') === dateKey
			))
			.sort((runA, runB) => new Date(runA.planned) - new Date(runB.planned))
	), [allowedRunIdSet, data?.getServiceRuns?.items, dateKey, employee?.id, timezone]);
	const activeRun = detailedRuns.find(run => String(run.id) === String(selectedRunId)) || detailedRuns[0] || null;
	const activeRunId = activeRun?.id ? String(activeRun.id) : null;
	const sortedDockets = useMemo(() => (
		[...(activeRun?.serviceDockets || [])].sort((docketA, docketB) => docketA.order - docketB.order)
	), [activeRun?.serviceDockets]);
	const firstIncompleteDocket = useMemo(() => (
		sortedDockets.find(docket => !isDocketHandledForMyDay(docket)) || sortedDockets[0] || null
	), [sortedDockets]);
	const activeDocket = sortedDockets.find(docket => String(docket.id) === String(selectedDocketId)) || firstIncompleteDocket;
	const employeeClockSummary = activeRun?.driverClockSummary ?? detailedRuns
		.find(run => run.driverClockSummary !== undefined)?.driverClockSummary;
	const clockStatus = useMemo(() => {
		const hasReliableClockState = typeof employeeClockSummary?.isClockedIn === 'boolean';
		const isClockedIn = hasReliableClockState
			? employeeClockSummary.isClockedIn
			: Boolean(employeeClockSummary?.clockedInAt && !employeeClockSummary?.clockedOutAt);
		return {
			isClockedIn,
			hasClockedOutToday: Boolean(!isClockedIn && employeeClockSummary?.clockedOutAt),
		};
	}, [employeeClockSummary]);
	const allAssignedRunsComplete = useMemo(() => (
		allowedRunIds.length > 0 &&
		detailedRuns.length === allowedRunIds.length &&
		detailedRuns.every(run => (
			(run?.serviceDockets || []).length > 0 &&
			(run.serviceDockets || []).every(docket => isDocketHandledForMyDay(docket))
		))
	), [allowedRunIds.length, detailedRuns]);
	const clockOutReminder = useMemo(() => {
		if (!employeeClockSummary) {
			return null;
		}

		const reminderDueAt = employeeClockSummary.reminderDueAt
			? new Date(employeeClockSummary.reminderDueAt).getTime()
			: NaN;
		return {
			returnedToWarehouseAt: employeeClockSummary.returnedToWarehouseAt || null,
			reminderDueAt: employeeClockSummary.reminderDueAt || null,
			reminderResolvedAt: employeeClockSummary.reminderResolvedAt || null,
			reminderActive: Boolean(employeeClockSummary.returnedToWarehouseAt &&
				Number.isFinite(reminderDueAt) &&
				reminderDueAt <= previewedAt &&
				!employeeClockSummary.reminderResolvedAt),
			// A preview exposes recorded state only and can never confirm a return.
			canConfirmReturnedToWarehouse: false,
		};
	}, [employeeClockSummary, previewedAt]);
	const checklistSubmissions = useMemo(() => (
		activeRun?.checklistSubmissions || []
	), [activeRun?.checklistSubmissions]);
	const startSteps = useMemo(() => (activeRun ? buildStartOfDaySteps({
		run: activeRun,
		clockStatus,
		tenantSettings,
		checklistSubmissions,
		todayDateKey: dateKey,
		timezone,
		isDocketComplete: isDocketDoneForMyDay,
	}) : []), [activeRun, checklistSubmissions, clockStatus, dateKey, tenantSettings, timezone]);
	const endSteps = useMemo(() => (activeRun ? buildEndOfDaySteps({
		run: activeRun,
		sortedDockets,
		checklistSubmissions,
		tenantSettings,
		clock: clockStatus,
		clockOutReminder,
		allAssignedRunsComplete,
		timezone,
	}) : []), [
		activeRun,
		allAssignedRunsComplete,
		checklistSubmissions,
		clockOutReminder,
		clockStatus,
		sortedDockets,
		tenantSettings,
		timezone,
	]);
	const perStopSteps = useMemo(() => buildPerStopSteps(activeDocket).map(step => ({
		...step,
		key: step.id,
		status: step.done
			? MY_DAY_STEP_STATUS.DONE
			: step.blocked ? MY_DAY_STEP_STATUS.BLOCKED : MY_DAY_STEP_STATUS.TODO,
	})), [activeDocket]);
	const runStarted = useMemo(() => isServiceRunStarted(activeRun), [activeRun]);
	const nextAction = useMemo(() => getNextAction({
		startSteps,
		activeDocket: firstIncompleteDocket && !isDocketHandledForMyDay(firstIncompleteDocket)
			? firstIncompleteDocket
			: null,
		perStopSteps: buildPerStopSteps(firstIncompleteDocket),
		endSteps,
		runStarted,
	}), [endSteps, firstIncompleteDocket, runStarted, startSteps]);
	const nextActionCopy = useMemo(() => getNextActionCopy({
		nextAction,
		activeDocket: firstIncompleteDocket,
	}), [firstIncompleteDocket, nextAction]);
	const startCountableSteps = startSteps.filter(step => COUNTABLE_STEP_STATUSES.includes(step.status));
	const startOfDayComplete = startCountableSteps.length > 0 && startCountableSteps.every(step => (
		step.status === MY_DAY_STEP_STATUS.DONE
	));
	const showStopsSection = runStarted || startOfDayComplete;
	const stopsAsSteps = sortedDockets.map(docket => ({
		status: isDocketDoneForMyDay(docket) ? MY_DAY_STEP_STATUS.DONE : MY_DAY_STEP_STATUS.TODO,
	}));
	const employeeName = getEmployeeName(employee);
	const dateLabel = formatDate(getLocalTime(timezone, dateKey), 'DAY_MONTHWORD_YEAR');
	const dataWarning = error
		? 'Some details for this employee could not be loaded. Available recorded data is shown below.'
		: (!loading && detailedRuns.length < allowedRunIds.length)
			? 'One or more runs are no longer available for this employee today.'
			: null;


	return {
		isOnline,
		timezone,
		loading,
		error,
		refreshPreview,
		isRefreshing,
		detailedRuns,
		activeRun,
		activeRunId,
		setSelectedRunId,
		setSelectedDocketId,
		sortedDockets,
		activeDocket,
		startSteps,
		endSteps,
		perStopSteps,
		runStarted,
		nextAction,
		nextActionCopy,
		showStopsSection,
		stopsAsSteps,
		employeeName,
		dateLabel,
		dataWarning,
	};
};

export default useEmployeeDayPreview;
